import { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useOnClickOutside from '../../job_change_processes/hooks/useOnClickOutside';
import { BlockButton } from '@/components/BlockButton';
import clsx from 'clsx';
import { MdCalendarToday } from 'react-icons/md';

interface CandidatesDateFieldForFormProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

// カスタムヘッダーのコンポーネント
const renderCustomHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  changeYear,
}: {
  date: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  changeYear: (year: number) => void;
}) => {
  const month = new Intl.DateTimeFormat('ja', { month: 'long' }).format(date);

  // 年選択用のオプション
  const currentYear = date.getFullYear();
  const yearOptions = [];
  for (let i = currentYear - 80; i <= currentYear + 20; i++) {
    yearOptions.unshift(i);
  }

  return (
    <div className="flex items-center justify-between px-2">
      <button
        type="button"
        className="size-lg"
        onClick={(e) => {
          e.stopPropagation();
          decreaseMonth();
        }}
      >
        <img
          src="/agent/job_change_processes/icon-chevron-left.svg"
          alt=""
          width={24}
          height={24}
        />
      </button>
      <div className="flex items-center gap-2">
        <select
          value={currentYear}
          onChange={(e) => {
            e.stopPropagation();
            changeYear(parseInt(e.target.value));
          }}
          className="cursor-pointer rounded border-none bg-transparent px-1 text-base font-bold text-sub outline-hidden hover:bg-gray-100"
        >
          {yearOptions.map((yearOption) => (
            <option key={yearOption} value={yearOption}>
              {yearOption}
            </option>
          ))}
        </select>
        <span className="text-base font-bold text-sub">{month}</span>
      </div>
      <button
        type="button"
        className="size-lg"
        onClick={(e) => {
          e.stopPropagation();
          increaseMonth();
        }}
      >
        <img
          src="/agent/job_change_processes/icon-chevron-right.svg"
          alt=""
          width={24}
          height={24}
        />
      </button>
    </div>
  );
};

/**
 * 候補者登録画面専用のデイトピッカーコンポーネント（日付のみ）
 * CalendarDatetimeMenuをベースに時間選択部分を削除
 */
export default function CandidatesDateFieldForForm({
  value,
  onChange,
  placeholder = '日付を選択',
  className = '',
}: CandidatesDateFieldForFormProps) {
  const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | undefined>();
  const dateMenuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(dateMenuRef, () => setIsDateMenuOpen(false));

  // 現在の値をDateに変換
  const currentDate = value ? new Date(value) : undefined;

  // DateからYYYY-MM-DD形式の文字列に変換
  const dateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 表示用フォーマット（YYYY/MM/DD）
  const formatDisplayDate = (dateString: string): string => {
    if (!dateString) return '';
    return dateString.replace(/-/g, '/');
  };

  const handleDateChange = (date: Date | null) => {
    setTempSelectedDate(date || undefined);
  };

  const handleSubmit = () => {
    if (tempSelectedDate) {
      const dateString = dateToString(tempSelectedDate);
      onChange(dateString);
    }
    setIsDateMenuOpen(false);
    setTempSelectedDate(undefined);
  };

  const handleClear = () => {
    onChange('');
    setIsDateMenuOpen(false);
    setTempSelectedDate(undefined);
  };

  const handleMenuOpen = () => {
    setTempSelectedDate(currentDate);
    setIsDateMenuOpen(true);
  };

  const displayText = value ? formatDisplayDate(value) : placeholder;

  return (
    <div className={`relative ${className}`} ref={dateMenuRef}>
      <button
        type="button"
        className={clsx(
          'flex h-[42px] w-full items-center rounded border border-solid border-border bg-white px-3 focus:border-2 focus:border-primary focus-visible:outline-hidden',
          isDateMenuOpen && 'border-2! border-primary!'
        )}
        onClick={handleMenuOpen}
      >
        <span className={clsx('text-sm', !value && 'text-gray-500')}>{displayText}</span>
        <MdCalendarToday className="ml-auto size-5 text-gray-400" />
      </button>

      {isDateMenuOpen && (
        <div className="absolute z-[2] mt-1">
          <div className="border-2 border-solid border-border bg-white">
            <div className="w-[236px] border-b border-solid border-border">
              <DatePicker
                selected={tempSelectedDate}
                dateFormat="yyyy年MM月dd日"
                locale={'ja'}
                inline
                onChange={handleDateChange}
                calendarClassName="calendar-datetime-menu"
                renderCustomHeader={(params) => {
                  return renderCustomHeader({
                    date: params.date,
                    decreaseMonth: params.decreaseMonth,
                    increaseMonth: params.increaseMonth,
                    changeYear: params.changeYear,
                  });
                }}
              />
            </div>
            <div className="flex h-[56px] items-center justify-end gap-1 bg-white px-3">
              <div className="w-[52px]">
                <BlockButton
                  colorType="white"
                  height={32}
                  type="button"
                  text="クリア"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                />
              </div>
              <div className="w-xxl">
                <BlockButton
                  height={32}
                  type="button"
                  text="決定"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubmit();
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
